import { useEffect, useRef } from 'react';

/**
 * Global ticker state to be shared across all components
 */
interface GlobalTickerState {
    listeners: Set<(timestamp: number) => void>;
    intervalId: number | null;
    tickRate: number;
    lastTimestamp: number;
    stats: {
        totalListeners: number;
        activeListeners: number;
        skippedListeners: number;
        lastUpdateTime: number;
    };
    skipConditions: Map<(timestamp: number) => void, () => boolean>;
    isInitialized: boolean;
    listenerNames: Map<(timestamp: number) => void, string>; // Track listener names for debugging
}

const TICK_RATE = 1000;
// Single global state for the ticker system
const globalTickerState: GlobalTickerState = {
    listeners: new Set(),
    intervalId: null,
    tickRate: TICK_RATE, // Default tick rate: 1 second
    lastTimestamp: Date.now(),
    stats: {
        totalListeners: 0,
        activeListeners: 0,
        skippedListeners: 0,
        lastUpdateTime: Date.now()
    },
    skipConditions: new Map(),
    isInitialized: false,
    listenerNames: new Map() // Map to store listener names/identifiers
};

/**
 * Starts the global ticker if it's not already running
 */
const startGlobalTicker = () => {
    if (globalTickerState.intervalId !== null) return;

    globalTickerState.intervalId = window.setInterval(() => {
        const timestamp = Date.now();
        globalTickerState.lastTimestamp = timestamp;
        globalTickerState.stats.lastUpdateTime = timestamp;

        // Reset active and skipped counts for this tick
        globalTickerState.stats.activeListeners = 0;
        globalTickerState.stats.skippedListeners = 0;
        // Notify all listeners with the current timestamp
        globalTickerState.listeners.forEach(listener => {
            try {
                // Check if this listener should be skipped
                const skipCondition = globalTickerState.skipConditions.get(listener);
                if (skipCondition) {
                    const shouldSkip = skipCondition();

                    if (shouldSkip) {
                        globalTickerState.stats.skippedListeners++;
                        return; // Skip this listener
                    }
                }

                // Execute the listener
                globalTickerState.stats.activeListeners++;
                listener(timestamp);
            } catch (error) {
                console.error('Error in global ticker listener:', error);
            }
        });

    }, globalTickerState.tickRate);
};

/**
 * Stops the global ticker if there are no more listeners
 */
const stopGlobalTickerIfNeeded = () => {
    if (globalTickerState.listeners.size === 0 && globalTickerState.intervalId !== null) {
        window.clearInterval(globalTickerState.intervalId);
        globalTickerState.intervalId = null;
    }
};

/**
 * Initialize the global ticker at application startup
 * Should be called once at the root level of the application
 */
export const initializeGlobalTicker = () => {
    if (globalTickerState.isInitialized) return;
    startGlobalTicker();
    globalTickerState.isInitialized = true;

    return () => {
        // Provide a cleanup function that can be called when the app unmounts
        if (globalTickerState.intervalId !== null) {
            window.clearInterval(globalTickerState.intervalId);
            globalTickerState.intervalId = null;
        }
    };
};

/**
 * Updates the global ticker rate and restarts the interval if needed
 */
export const setGlobalTickRate = (newTickRate: number) => {
    if (newTickRate <= 0) {
        console.error('Tick rate must be greater than 0');
        return;
    }

    globalTickerState.tickRate = newTickRate;

    // Restart the interval with the new tick rate if it's running
    if (globalTickerState.intervalId !== null) {
        window.clearInterval(globalTickerState.intervalId);
        globalTickerState.intervalId = null;
        startGlobalTicker();
    }
};

/**
 * Get current stats about the global ticker
 */
export const getGlobalTickerStats = () => {
    return {
        ...globalTickerState.stats,
        totalListeners: globalTickerState.listeners.size,
        isRunning: globalTickerState.intervalId !== null,
        tickRate: globalTickerState.tickRate,
        isInitialized: globalTickerState.isInitialized
    };
};

/**
 * Subscribe a callback function to the global ticker
 * This can be used outside of React components
 * @returns A function to unsubscribe the callback
 */
export const subscribeToGlobalTicker = (
    callback: (timestamp: number) => void,
    skipCondition?: () => boolean,
    listenerName: string = 'anonymous'
) => {
    // Add the listener to the global state
    globalTickerState.listeners.add(callback);
    globalTickerState.stats.totalListeners = globalTickerState.listeners.size;
    globalTickerState.listenerNames.set(callback, listenerName);

    // Add skip condition if provided
    if (skipCondition) {
        globalTickerState.skipConditions.set(callback, skipCondition);
    }

    // Start the ticker if it's not already running and not explicitly initialized
    if (!globalTickerState.isInitialized) {
        startGlobalTicker();
    }

    // Return unsubscribe function
    return () => {
        globalTickerState.listeners.delete(callback);
        globalTickerState.skipConditions.delete(callback);
        globalTickerState.listenerNames.delete(callback);
        globalTickerState.stats.totalListeners = globalTickerState.listeners.size;

        // Only stop the ticker if it wasn't explicitly initialized
        if (!globalTickerState.isInitialized) {
            stopGlobalTickerIfNeeded();
        }
    };
};

/**
 * Custom hook that subscribes to the global ticker
 * @param callback Function to call on each tick with the current timestamp
 * @param dependencies Optional array of dependencies to control when the callback is updated
 * @param skipCondition Optional function that returns true if the ticker should skip this component
 * @param componentName Optional name for the component for debugging purposes
 */
export const useGlobalTicker = (
    callback: (timestamp: number) => void,
    dependencies: any[] = [],
    skipCondition?: () => boolean,
    componentName: string = 'UnnamedComponent'
) => {
    // Use a ref to store the latest callback
    const callbackRef = useRef(callback);
    const skipConditionRef = useRef(skipCondition);
    // Update the callback ref when the callback changes
    useEffect(() => {
        callbackRef.current = callback;
    }, [callback, ...dependencies]);

    // Update the skip condition ref when it changes
    useEffect(() => {
        const oldSkipCondition = skipConditionRef.current;
        skipConditionRef.current = skipCondition;

    }, [skipCondition, componentName]);

    useEffect(() => {
        // Create a wrapper function that calls the latest callback
        const listenerFn = (timestamp: number) => {
            callbackRef.current(timestamp);
        };
        // Use the subscription function to add the listener
        const unsubscribe = subscribeToGlobalTicker(
            listenerFn,
            skipConditionRef.current,
            componentName
        );

        // Immediately call the callback with the current timestamp if not skipped
        if (!skipConditionRef.current || !skipConditionRef.current()) {
            listenerFn(globalTickerState.lastTimestamp);
        }

        // Clean up when the component unmounts
        return () => {
            unsubscribe();
        };
    }, []); // Empty dependency array ensures this effect runs only once
};

export default useGlobalTicker; 